import React from 'react';
import {
  AccountBalance,
  Loop,
  TrendingUp,
  AccountBalanceWallet,
  HourglassBottom,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';

/**
 * Individual KPI card with icon, value, label, and optional trend.
 */
const KPICard = ({ icon, label, value, subtitle, color, trend }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-lg transition-all duration-300 group">
    <div className="flex items-start justify-between mb-3">
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${color}15` }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
      </div>
      {trend !== undefined && (
        <Tooltip title={`${trend >= 0 ? '+' : ''}${trend}% vs last month`} arrow>
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              trend >= 0
                ? 'bg-green-50 text-green-600'
                : 'bg-red-50 text-red-600'
            }`}
          >
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        </Tooltip>
      )}
    </div>
    <p className="text-2xl font-bold text-gray-900 mb-0.5">{value}</p>
    <p className="text-sm font-medium text-gray-600">{label}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

/**
 * Skeleton loader for KPI cards.
 */
const KPICardSkeleton = () => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 animate-pulse">
    <div className="w-11 h-11 rounded-xl bg-gray-200 mb-3" />
    <div className="h-7 bg-gray-200 rounded w-1/2 mb-2" />
    <div className="h-4 bg-gray-100 rounded w-2/3 mb-1" />
    <div className="h-3 bg-gray-50 rounded w-1/2" />
  </div>
);

/**
 * Format number as Indian currency string.
 */
const formatCurrency = (amount) => {
  if (amount == null) return '₹0';
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount.toLocaleString('en-IN')}`;
};

/**
 * KPISummaryCards
 *
 * 5 top-level KPI cards:
 *   1. Total Credit Exposure
 *   2. Active Credit Cycles
 *   3. Disbursement This Month
 *   4. Outstanding Principal
 *   5. Settlements Pending
 */
const KPISummaryCards = ({ data = {}, loading = false }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <KPICardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const cards = [
    {
      icon: <AccountBalance />,
      label: 'Total Credit Exposure',
      value: formatCurrency(data.totalCreditExposure ?? 0),
      subtitle: `${data.totalApplications ?? 0} total applications`,
      color: '#6366f1',
      trend: data.exposureTrend,
    },
    {
      icon: <Loop />,
      label: 'Active Credit Cycles',
      value: data.activeCreditCycles ?? 0,
      subtitle: `${data.completedCycles ?? 0} completed`,
      color: '#0ea5e9',
      trend: data.cyclesTrend,
    },
    {
      icon: <TrendingUp />,
      label: 'Disbursement This Month',
      value: formatCurrency(data.disbursementThisMonth ?? 0),
      subtitle: `${data.disbursementCount ?? 0} disbursements`,
      color: '#10b981',
      trend: data.disbursementTrend,
    },
    {
      icon: <AccountBalanceWallet />,
      label: 'Outstanding Principal',
      value: formatCurrency(data.outstandingPrincipal ?? 0),
      subtitle: `${data.overdueCount ?? 0} overdue`,
      color: '#f59e0b',
      trend: data.principalTrend,
    },
    {
      icon: <HourglassBottom />,
      label: 'Settlements Pending',
      value: data.settlementsPending ?? 0,
      subtitle: formatCurrency(data.settlementsPendingAmount ?? 0) + ' value',
      color: '#ef4444',
      trend: data.settlementsTrend,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((c, i) => (
        <KPICard key={i} {...c} />
      ))}
    </div>
  );
};

export default KPISummaryCards;
export { formatCurrency };
